import React, { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  InputBaseComponentProps,
  MenuItem,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import ClearIcon from "@mui/icons-material/Clear";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
  borderRadius,
  defaultStyle,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import StewartInput from "controls/global/stewart-input";
import StewartSelect from "controls/global/stewart-select/StewartSelect";
import StewartAutocompleteWithCheckboxes, {
  OptionRenderType,
} from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import NumberInputPricing from "controls/global/number-input-field/pricing/NumberInputPricing";
import DatePickerSearch from "controls/global/datepicker-search/DatePickerSearch";
import { parse } from "date-fns";
import { formatDateToJSONString, isValidDate } from "utils/shared";
import { Search } from "@mui/icons-material";

const Panel = styled(Box)({
  padding: padding.large1,
  backgroundColor: colors.white,
  borderRadius: borderRadius.small,
  border: `1px solid ${colors.grey09}`,
  "& .MuiInputBase-root": {
    height: 40,
    minHeight: 40,
  },
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 40,
  },
  "& .MuiAutocomplete-tag": {
    margin: "2px 4px",
    height: 24,
  },
  // Panel forces 40px inputs; MUI's default un-shrunk label transform is
  // calibrated for a 56px input and sits too low. Mirrors the same fix used
  // by PolicyManagementHome.tsx and InventoryLookupGrid.tsx for consistency.
  "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
    transform: "translate(14px, 11px) scale(1)",
  },
  "& .MuiFormHelperText-root": {
    position: "absolute",
    top: "100%",
    margin: 0,
    whiteSpace: "nowrap",
  },
});

const Row = styled(Box)({
  display: "flex",
  gap: gaps.large1,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: gaps.medium1,
});

const Field = styled(Box)({
  flex: "1 1 200px",
  minWidth: "180px",
  maxWidth: "260px",
  position: "relative",
});

const FieldError = styled(Typography)({
  fontSize: fontSize.small,
  color: defaultStyle.control.errorColor,
  position: "absolute",
  top: "100%",
  whiteSpace: "nowrap",
});

export type AdvancedSearchPanelFieldType =
  | "text"
  | "date"
  | "multi"
  | "select"
  | "separator"
  | "number"
  | "label";

export interface AdvancedSearchPanelField {
  key: string;
  label: string;
  type: AdvancedSearchPanelFieldType;
  options?: OptionRenderType[];
  inline?: boolean;
  placeholder?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  minValue?: number | null;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  reset?: number;
  inputType?: "text" | "number";
  inputProps?: InputBaseComponentProps;
  inputComponent?: React.ElementType;
}

export interface AdvancedSearchPanelSection {
  title: string;
  fields: AdvancedSearchPanelField[];
}

export function parseIsoDate(value: unknown): Date | null {
  if (typeof value !== "string" || !value) return null;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValidDate(parsed) ? parsed : null;
}

interface AdvancedSearchPanelProps {
  open: boolean;
  sections: AdvancedSearchPanelSection[];
  values: Record<string, string | OptionRenderType[]>;
  onChange: (key: string, value: string | OptionRenderType[]) => void;
  onClear?: () => void;
  onSearch?: () => void;

  searchDisabled?: boolean;
  actionsOnOwnRow?: boolean;
}

// Mirrors the `Lookup` tab's Date Type styling on the Policy Inventory page
// (controls/global/advanced-search/DateTypeField.tsx) — flat (no underline),
// click toggles the menu open/closed, selecting an item closes it.
const AdvancedSelectField = ({
  field,
  value,
  onChange,
}: {
  field: AdvancedSearchPanelField;
  value: string;
  onChange: (key: string, value: string | OptionRenderType[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <StewartSelect
      select
      fullWidth
      name={field.key}
      id={field.key}
      label={field.label}
      value={value}
      InputProps={{
        disableUnderline: true,
        style: { color: colors.black },
        endAdornment: value ? (
          <IconButton
            aria-label="Clear date type"
            onClick={(e) => {
              e.stopPropagation();
              onChange(field.key, "");
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ) : undefined,
      }}
      SelectProps={{ open }}
      onClick={() => setOpen((o) => !o)}
      onChange={(e) => {
        onChange(field.key, e.target.value);
        setOpen(false);
      }}
    >
      {(field.options ?? []).map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </StewartSelect>
  );
};

const AdvancedSearchPanel = ({
  open,
  sections,
  values,
  onChange,
  onClear,
  onSearch,
  searchDisabled,
  actionsOnOwnRow,
}: AdvancedSearchPanelProps) => {
  // Bumped on every Clear so each field's React key changes, forcing a fresh
  // mount — needed because StewartAutocompleteWithCheckboxes and
  // DatePickerSearch keep internal state that doesn't re-sync from props.
  const [resetCounter, setResetCounter] = useState(0);
  const handleClear = onClear
    ? () => {
        setResetCounter((c) => c + 1);
        onClear();
      }
    : undefined;

  const hasRangeError = sections.some((section) =>
    section.fields.some((field) => {
      if (field.error) return true;
      if (field.type !== "number" || field.minValue == null) return false;
      const raw = values[field.key];
      if (typeof raw !== "string" || raw === "") return false;
      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) && parsed < field.minValue;
    })
  );

  const clearButton = handleClear && (
    <ActionButton
      variant="outlined"
      color="secondary"
      startIcon={<ClearIcon />}
      onClick={handleClear}
    >
      Clear
    </ActionButton>
  );

  const searchButton = onSearch && (
    <ActionButton
      variant={actionsOnOwnRow ? "contained" : "outlined"}
      color={actionsOnOwnRow ? "primary" : "secondary"}
      startIcon={<Search />}
      disabled={hasRangeError || !!searchDisabled}
      onClick={onSearch}
    >
      Search
    </ActionButton>
  );

  return (
    <Collapse in={open} unmountOnExit>
      <Panel>
        {sections.map((section, sIdx) => {
          const isLast = sIdx === sections.length - 1;
          return (
            <Box key={section.title} sx={{ marginBottom: gaps.medium1 }}>
              <Row>
                {section.fields.map((field) => {
                  const value = values[field.key];
                  const fieldKey = `${field.key}-${resetCounter}`;
                  if (field.type === "separator") {
                    return (
                      <Box
                        key={fieldKey}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: 40,
                          padding: `0 ${gaps.xsmall2}`,
                          color: colors.grey11,
                          fontSize: fontSize.large,
                          userSelect: "none",
                        }}
                      >
                        {field.label || "—"}
                      </Box>
                    );
                  }
                  if (field.type === "label") {
                    return (
                      <Box
                        key={fieldKey}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: "120px",
                            height: 40,
                            fontSize: fontSize.large,
                            fontWeight: fontWeight.bold1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {field.label}
                        </Box>
                    );
                  }
                  if (field.type === "multi") {
                    const arrayValue = Array.isArray(value) ? value : [];
                    return (
                      <Field key={fieldKey}>
                        <StewartAutocompleteWithCheckboxes
                          name={field.key}
                          placeholder={field.label}
                          options={field.options || []}
                          defaultSelectedOption={arrayValue}
                          onChange={(_e, next) => onChange(field.key, next)}
                          enableClearable
                        />
                      </Field>
                    );
                  }
                  if (field.type === "select") {
                    const strValue = typeof value === "string" ? value : "";
                    return (
                      <Field key={fieldKey}>
                        <AdvancedSelectField
                          field={field}
                          value={strValue}
                          onChange={onChange}
                        />
                      </Field>
                    );
                  }
                  if (field.type === "date") {
                    return (
                      <Field key={fieldKey}>
                        <DatePickerSearch
                          value={parseIsoDate(value)}
                          minDate={field.minDate}
                          maxDate={field.maxDate}
                          disabled={field.disabled}
                          hasError={field.error}
                          reset={field.reset}
                          handleDateChange={(newDate: Date | null) => {
                            const iso = formatDateToJSONString(newDate, true);
                            onChange(
                              field.key,
                              typeof iso === "string" ? iso.slice(0, 10) : ""
                            );
                          }}
                        />
                        {field.error && field.errorText && (
                          <FieldError>{field.errorText}</FieldError>
                        )}
                      </Field>
                    );
                  }
                  if (field.type === "number") {
                    const numValue =
                      typeof value === "string" && value !== ""
                        ? parseFloat(value)
                        : 0;

                    const belowMin =
                      field.minValue != null && !!value && numValue < field.minValue;
                    const hasError = field.error || belowMin;
                    return (
                      <Field key={fieldKey}>
                        <NumberInputPricing
                          name={field.key}
                          label={field.label || field.placeholder}
                          value={Number.isFinite(numValue) ? numValue : 0}
                          placeholder={field.placeholder ?? "$0.00"}
                          prefix="$"
                          decimalScale={2}
                          error={hasError}
                          helperText={
                            field.error
                              ? field.errorText
                              : belowMin
                              ? `Should not be less than ${field.minValue}`
                              : undefined
                          }
                          styles={{}}
                          onChange={(
                            e: React.ChangeEvent<
                              HTMLInputElement | HTMLTextAreaElement
                            >
                          ) => {
                            const raw = (e.target as HTMLInputElement).value;
                            const isEmpty =
                              raw === "" ||
                              raw == null ||
                              Number.isNaN(raw as unknown as number);
                            onChange(field.key, isEmpty ? "" : String(raw));
                          }}
                        />
                      </Field>
                    );
                  }
                  const strValue = typeof value === "string" ? value : "";
                  return (
                    <Field key={fieldKey}>
                      <StewartInput
                        name={field.key}
                        id={field.key}
                        label={field.label}
                        type={field.inputType ?? "text"}
                        value={strValue}
                        placeholder={field.placeholder}
                        disabled={field.disabled}
                        error={field.error}
                        helperText={field.error ? field.errorText : undefined}
                        inputProps={field.inputProps}
                        InputProps={
                          field.inputComponent
                            ? { inputComponent: field.inputComponent as any }
                            : undefined
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(field.key, e.target.value)
                        }
                        fullWidth
                      />
                    </Field>
                  );
                })}
                {!actionsOnOwnRow && isLast && clearButton}
                {!actionsOnOwnRow && isLast && searchButton}
              </Row>
            </Box>
          );
        })}
        {actionsOnOwnRow && (
          <Row sx={{ justifyContent: "flex-end", marginBottom: 0 }}>
            {clearButton}
            {searchButton}
          </Row>
        )}
      </Panel>
    </Collapse>
  );
};

export default AdvancedSearchPanel;
